import { MessageDescriptor } from 'react-intl';

import { isString } from '../../../../../utilities/utils';
import { ProviderField } from '../../../../../models/provider';

import { messages } from './Input/messages';

// original types from old react-intl version
type MessageValue = string | number | boolean | Date | null | undefined;

export type ValidationValues = {
  isValid: boolean;
  errorMessage?: MessageDescriptor | string;
  errorMessageValues?: { [key: string]: MessageValue };
};

export type ValidateResponse = {
  onSubmit: ValidationValues;
  onType: ValidationValues;
};

export class ProviderInputValidator {
  providerField: ProviderField;

  constructor(providerField: ProviderField) {
    this.providerField = providerField;
  }

  static invalidResponse(
    errorMessage?: MessageDescriptor | string,
    errorMessageValues?: { [key: string]: MessageValue }
  ): ValidateResponse {
    return {
      onSubmit: { isValid: false, errorMessage, errorMessageValues },
      onType: { isValid: false, errorMessage, errorMessageValues },
    };
  }

  static validResponse() {
    return {
      onSubmit: { isValid: true },
      onType: { isValid: true },
    };
  }

  validate(value: string): ValidateResponse {
    const {
      optional,
      numeric,
      minLength,
      maxLength,
      pattern: matchPattern,
      patternError,
      selectOptions,
    } = this.providerField;

    if (optional) {
      return ProviderInputValidator.validResponse();
    }

    if (selectOptions) {
      const validValues = selectOptions.map((option) => option.value);

      if (!validValues.includes(value)) {
        return ProviderInputValidator.invalidResponse(messages.canNotBeEmpty);
      }
    }

    if (!isString(value) || value.length < 1) {
      return ProviderInputValidator.invalidResponse(messages.canNotBeEmpty);
    }

    const numberRegex = /^\d+$/;

    if (numeric && !numberRegex.test(value)) {
      return ProviderInputValidator.invalidResponse(messages.onlyDigits);
    }

    if (maxLength && value.length > maxLength) {
      return ProviderInputValidator.invalidResponse(messages.noLongerThan, { maxLength });
    }

    if (minLength && value.length < minLength) {
      // While the user is typing, the value cannot be too short
      return {
        onSubmit: {
          ...ProviderInputValidator.invalidResponse(messages.tooShort, { minLength }).onSubmit,
          isValid: false,
        },
        onType: { isValid: true },
      };
    }

    if (matchPattern) {
      const regex = new RegExp(matchPattern);
      const choppedValue = maxLength ? value.substring(0, maxLength) : value;

      if (regex.test(choppedValue)) {
        return ProviderInputValidator.validResponse();
      }

      return ProviderInputValidator.invalidResponse(patternError || '');
    }

    return ProviderInputValidator.validResponse();
  }
}
